
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DialogFooter,
} from '@/components/ui/dialog';
import { Bike } from '@/services/types';
import { MapPin, Gauge, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { toggleBikeMovementBlock, setBikeSpeedLimit } from '@/pages/admin/bikes/services/movementControlApi';

interface EditBikeFormProps {
  bike: Bike;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  loading: boolean;
}

export const EditBikeForm: React.FC<EditBikeFormProps> = ({ bike, onSubmit, loading }) => {
  const [movementBlocked, setMovementBlocked] = useState(bike.movement_blocked || false);
  const [speedLimit, setSpeedLimit] = useState<string>(bike.speed_limit?.toString() || '');
  const [isUpdatingMovement, setIsUpdatingMovement] = useState(false);

  // Обновляем локальное состояние при изменении данных велосипеда
  useEffect(() => {
    console.log('=== ОБНОВЛЕНИЕ СОСТОЯНИЯ ФОРМЫ ===');
    console.log('Данные велосипеда:', {
      id: bike.id,
      model: bike.model,
      movement_blocked: bike.movement_blocked,
      speed_limit: bike.speed_limit
    });
    setMovementBlocked(bike.movement_blocked || false);
    setSpeedLimit(bike.speed_limit?.toString() || '');
    console.log('Установлено локальное состояние:', {
      movementBlocked: bike.movement_blocked || false,
      speedLimit: bike.speed_limit?.toString() || ''
    });
  }, [bike.movement_blocked, bike.speed_limit, bike.id]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    console.log('=== ОТПРАВКА ОСНОВНОЙ ФОРМЫ ===');
    console.log('Локальное состояние перед отправкой:', {
      movementBlocked,
      speedLimit
    });
    
    // Добавляем скрытые поля для управления движением в основную форму
    const formData = new FormData(event.currentTarget);
    formData.set('movement_blocked', movementBlocked.toString());
    formData.set('speed_limit', speedLimit || '');
    
    console.log('Данные формы с управлением движением:', {
      movement_blocked: formData.get('movement_blocked'),
      speed_limit: formData.get('speed_limit')
    });
    
    // Передаем обработку родительскому компоненту для основных полей
    onSubmit(event);
  };

  const validateSpeedLimit = (value: string) => {
    if (!value) return true; // Пустое значение разрешено
    
    const limit = parseInt(value);
    const maxSpeed = bike.max_allowed_speed || 25;
    
    if (isNaN(limit) || limit < 1 || limit > maxSpeed) {
      toast.error(`Скорость должна быть от 1 до ${maxSpeed} км/ч`);
      return false;
    }
    return true;
  };

  const handleSpeedLimitChange = (value: string) => {
    if (validateSpeedLimit(value)) {
      console.log('Изменение ограничения скорости:', value);
      setSpeedLimit(value);
    }
  };

  const handleMovementBlockedChange = async (checked: boolean) => {
    console.log('=== ИЗМЕНЕНИЕ БЛОКИРОВКИ ДВИЖЕНИЯ ===');
    console.log('Новое значение:', checked);
    console.log('ID велосипеда:', bike.id);
    
    setIsUpdatingMovement(true);
    try {
      const result = await toggleBikeMovementBlock(bike.id, checked);
      if (result.success) {
        setMovementBlocked(checked);
        // При блокировке сбрасываем ограничение скорости
        if (checked) {
          console.log('Сброс ограничения скорости при блокировке');
          setSpeedLimit('');
        }
        toast.success(`Изменения применены. Вебхук отправлен.`);
      } else {
        toast.error(`Ошибка: ${result.error}`);
        // Возвращаем предыдущее состояние при ошибке
        setMovementBlocked(!checked);
      }
    } catch (error: any) {
      console.error('Ошибка при изменении блокировки:', error);
      toast.error(`Ошибка: ${error.message}`);
      setMovementBlocked(!checked);
    } finally {
      setIsUpdatingMovement(false);
    }
  };

  const handleSpeedLimitApply = async () => {
    console.log('=== ПРИМЕНЕНИЕ ОГРАНИЧЕНИЯ СКОРОСТИ ===');
    console.log('Значение:', speedLimit);
    console.log('ID велосипеда:', bike.id);
    
    const limit = speedLimit ? parseInt(speedLimit) : null;
    
    if (speedLimit && !validateSpeedLimit(speedLimit)) {
      return;
    }

    setIsUpdatingMovement(true);
    try {
      const result = await setBikeSpeedLimit(bike.id, limit);
      if (result.success) {
        setMovementBlocked(false); // При установке ограничения снимаем полную блокировку
        toast.success(`Изменения применены. Вебхук отправлен.`);
      } else {
        toast.error(`Ошибка: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Ошибка при изменении ограничения скорости:', error);
      toast.error(`Ошибка: ${error.message}`);
    } finally {
      setIsUpdatingMovement(false);
    }
  };

  // Форматируем время последнего обновления
  const formattedLastUpdate = bike.last_movement_update ? 
    format(new Date(bike.last_movement_update), 'dd.MM.yyyy HH:mm:ss') : 
    'Никогда';

  console.log('=== РЕНДЕР ФОРМЫ ===');
  console.log('Текущее состояние формы:', {
    movementBlocked,
    speedLimit,
    bikeData: {
      movement_blocked: bike.movement_blocked,
      speed_limit: bike.speed_limit,
      last_movement_update: bike.last_movement_update
    }
  });

  const isFormDisabled = loading || isUpdatingMovement;

  return (
    <form onSubmit={handleFormSubmit}>
      {/* Скрытые поля для основной формы */}
      <input type="hidden" name="id" value={bike.id} />
      <input type="hidden" name="movement_blocked" value={movementBlocked.toString()} />
      <input type="hidden" name="speed_limit" value={speedLimit || ''} />
      
      <div className="grid gap-4 py-4">
        {/* Основная информация */}
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="editModel" className="text-right">
            Модель
          </Label>
          <Input
            id="editModel"
            name="editModel"
            defaultValue={bike.model}
            className="col-span-3"
            required
            disabled={isFormDisabled}
          />
        </div>
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="editGpsTag" className="text-right">
            GPS-метка
          </Label>
          <div className="col-span-3 flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <Input
              id="editGpsTag"
              name="editGpsTag"
              defaultValue={bike.gps_tag || ''}
              className="flex-1"
              placeholder="Введите идентификатор GPS-метки"
              disabled={isFormDisabled}
            />
          </div>
        </div>
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="editAmoNumber" className="text-right">
            AMO номер
          </Label>
          <Input
            id="editAmoNumber"
            name="editAmoNumber"
            defaultValue={bike.amo_number || ''}
            className="col-span-3"
            placeholder="Идентификатор в CRM"
            disabled={isFormDisabled}
          />
        </div>
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="editMileage" className="text-right">
            Пробег (км)
          </Label>
          <Input
            id="editMileage"
            name="editMileage"
            type="number"
            defaultValue={bike.mileage || 0}
            className="col-span-3"
            placeholder="Введите пробег велосипеда"
            disabled={isFormDisabled}
          />
        </div>
        <div className="grid grid-cols-4 items-center gap-4">
          <Label htmlFor="editStatus" className="text-right">
            Статус
          </Label>
          <Select defaultValue={bike.status} name="editStatus" disabled={isFormDisabled}>
            <SelectTrigger className="col-span-3">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Доступен</SelectItem>
              <SelectItem value="in_use">Используется</SelectItem>
              <SelectItem value="maintenance">Ремонт</SelectItem>
              <SelectItem value="retired">Списан</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Separator className="my-4" />

        {/* Секция управления движением */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5" />
            <h3 className="text-lg font-medium">Управление движением</h3>
          </div>
          
          {/* Блокировка движения */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="movementBlocked" className="text-right">
              Блокировка движения
            </Label>
            <div className="col-span-3 flex items-center gap-2">
              <Switch
                id="movementBlocked"
                checked={movementBlocked}
                onCheckedChange={handleMovementBlockedChange}
                disabled={isFormDisabled}
              />
              <span className="text-sm text-muted-foreground">
                {movementBlocked ? 'Движение заблокировано' : 'Движение разрешено'}
              </span>
              {movementBlocked && (
                <AlertTriangle className="h-4 w-4 text-destructive" />
              )}
              {isUpdatingMovement && (
                <span className="text-xs text-blue-600">Обновляется...</span>
              )}
            </div>
          </div>

          {/* Ограничение скорости */}
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="speedLimitInput" className="text-right">
              Ограничение скорости
            </Label>
            <div className="col-span-3 space-y-2">
              <div className="flex gap-2">
                <Input
                  id="speedLimitInput"
                  type="number"
                  min="1"
                  max={bike.max_allowed_speed || 25}
                  value={speedLimit}
                  onChange={(e) => handleSpeedLimitChange(e.target.value)}
                  placeholder={`Макс. ${bike.max_allowed_speed || 25} км/ч`}
                  disabled={isFormDisabled || movementBlocked}
                  className="flex-1"
                />
                <Button 
                  type="button"
                  onClick={handleSpeedLimitApply}
                  disabled={isFormDisabled || movementBlocked}
                  size="sm"
                >
                  {isUpdatingMovement ? 'Применяется...' : 'Применить'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Оставьте пустым для снятия ограничения скорости
              </p>
            </div>
          </div>

          {movementBlocked && (
            <div className="col-span-4 bg-destructive/10 border border-destructive/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertTriangle className="h-4 w-4" />
                <span>Внимание: движение велосипеда полностью заблокировано</span>
              </div>
            </div>
          )}
          
          {/* Время последнего обновления */}
          <div className="col-span-4 flex items-center text-xs text-muted-foreground mt-2">
            <Clock className="h-3 w-3 mr-1" />
            <span>Последнее обновление настроек: {formattedLastUpdate}</span>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button type="submit" disabled={isFormDisabled}>
          {loading ? 'Сохранение...' : 'Сохранить все данные'}
        </Button>
      </DialogFooter>
    </form>
  );
};
